import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Portal from "app/components/portal";
import useModalFocus from "app/utils/hooks/useModalFocus";
import infoIcon from "app/images/circle-info-yellow.svg";
import styles from "./styles.module.scss";

interface StripeRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  onSetUpStripe: () => void;
}

/**
 * Shown when the coach opens "Create packages and subscriptions" from the
 * checklist while Stripe is unconnected (Figma 464:6650).
 */
const StripeRequiredModal = ({
  isOpen,
  onClose,
  onProceed,
  onSetUpStripe,
}: StripeRequiredModalProps) => {
  const { t } = useTranslation();
  const proceedRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  useModalFocus(isOpen, cardRef, proceedRef);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const title = t("Stripe Connection Required for Online Payments");

  return (
    <Portal>
      <div
        className={styles.overlay}
        onClick={onClose}
        data-testid="academy-setup-stripe-required-modal"
      >
        <div
          ref={cardRef}
          className={styles.card}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.content}>
            <img className={styles.icon} src={infoIcon} alt="" />
            <div className={styles.texts}>
              <h2 className={styles.title}>{title}</h2>
              <p className={styles.body}>
                {t(
                  "If you plan to accept payments online, connect Stripe now. Without a connected account, everything you create will be limited to in person payments. Connecting later enables online payments for new packages going forward, but NOT for what already exists.",
                )}
              </p>
            </div>
          </div>
          <div className={styles.buttons}>
            <button
              ref={proceedRef}
              type="button"
              className={styles.button}
              onClick={onProceed}
            >
              {t("Proceed to Packages")}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={onSetUpStripe}
            >
              {t("Set up Stripe")}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default StripeRequiredModal;
